import { Component, computed, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { OpportunityEstimateView } from '@api-proxies';
import { EstimateTypes } from '@models/enums/EstimateTypes';
import { OpportunityStatuses } from '@models/enums/OpportunityStatuses';
import { IconSanitizerService, NoDataComponent, TabItemComponent, TabViewComponent } from 'ngx-lib';
import { EstimatesChangeOrderGridComponent } from './estimates-change-order-grid/estimates-change-order-grid.component';
import { EstimatesGridComponent } from './estimates-grid/estimates-grid.component';
import { EstimatesSubHeaderComponent } from './estimates-sub-header/estimates-sub-header.component';
import { EstimatesSummaryComponent } from './estimates-summary/estimates-summary.component';

@Component({
    selector: 'app-opportunities-single-estimates',
    templateUrl: './opportunities-single-estimates.component.html',
    styleUrls: ['./opportunities-single-estimates.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NoDataComponent,
        TabViewComponent,
        TabItemComponent,
        EstimatesSummaryComponent,
        EstimatesSubHeaderComponent,
        EstimatesGridComponent,
        EstimatesChangeOrderGridComponent
    ]
})
export class OpportunitiesSingleEstimatesComponent {
    opportunityId = input.required<number>();
    manualId = input<number>();
    opportunityStatusId = input<number>();
    canCreateEstimate = input<boolean>(false);
    opportunityEstimates = input<OpportunityEstimateView[]>([]);

    createEstimate = output<void>();

    currentTab = signal('Final Estimates');
    showSummary = signal(false);
    isLoading = signal(true);
    requestedStatus = signal(OpportunityStatuses.BidRequested);

    alternateEstimatesIcon = signal<SafeHtml | undefined>(undefined);
    finalEstimatesIcon = signal<SafeHtml | undefined>(undefined);
    changeOrderIcon = signal<SafeHtml | undefined>(undefined);

    finalEstimateId = computed(() => {
        const estimates = this.opportunityEstimates();
        return estimates.find((estimate) => estimate.estimateTypeId === EstimateTypes.Final && estimate.isSelected)
            ?.estimateId;
    });

    emptyStateTitle = computed(() => {
        const statusId = this.opportunityStatusId();
        const manualId = this.manualId();

        if (statusId === OpportunityStatuses.Disqualified) {
            return 'Cannot create or link a bid\nwhile the opportunity is in Disqualified status';
        }

        if (manualId) {
            return 'Cannot create or link a bid\nwhile the opportunity is in manual status';
        }

        return 'You have not created any bids yet.';
    });

    emptyStateLink = computed(() => {
        const statusId = this.opportunityStatusId();
        const manualId = this.manualId();
        const canCreate = this.canCreateEstimate();

        if (statusId === OpportunityStatuses.Disqualified || manualId) {
            return 'Go to Overview';
        }

        return canCreate ? 'Please link a bid to get started' : '';
    });

    isManualOrDisqualified = computed(() => {
        const statusId = this.opportunityStatusId();
        const manualId = this.manualId();

        return manualId || statusId === OpportunityStatuses.Disqualified;
    });

    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly router = inject(Router);

    constructor() {
        this.alternateEstimatesIcon.set(this.iconSanitizer.getIcon('alternateEstimates'));
        this.finalEstimatesIcon.set(this.iconSanitizer.getIcon('finalEstimates'));
        this.changeOrderIcon.set(this.iconSanitizer.getIcon('changeOrder'));
    }

    createBaseEstimate() {
        if (!this.canCreateEstimate()) return;
        this.createEstimate.emit();
    }

    navigateTo(tabName: string) {
        this.router.navigate(['opportunities-single', this.opportunityId(), tabName]);
    }

    refreshPage() {
        window.location.reload();
    }
}
